import { addDays } from 'date-fns/addDays'
import { isBefore } from 'date-fns/isBefore'
import { isValid } from 'date-fns/isValid'
import { startOfDay } from 'date-fns/startOfDay'
import { z } from 'zod'

export const createSafeDate = (
  year: string | number = '',
  month: string | number = '',
  day: string | number = ''
): Date => {
  const parsedYear: number = typeof year === 'string' ? Number.parseInt(year) : year
  const parsedMonth: number = (typeof month === 'string' ? Number.parseInt(month) : month) - 1
  const parsedDay: number = typeof day === 'string' ? Number.parseInt(day) : day
  const date = new Date(parsedYear, parsedMonth, parsedDay)
  date.setHours(0, 0, 0, 0)
  return date
}
export const dateToString = (date: Date) =>
  [
    date.getFullYear(),
    (date.getMonth() + 1).toString().padStart(2, '0'),
    date.getDate().toString().padStart(2, '0'),
  ].join('-')

export const isValidDate = (date?: Date) => date && !Number.isNaN(Date.parse(date.toString()))

// TODO use date-fns for date utilities method
// https://hellogetsafe.atlassian.net/browse/PC-2164
// should return date difference between two dates
export const dateDiff = {
  inDays: (d1: Date, d2: Date) => {
    const t2 = d2.getTime()
    const t1 = d1.getTime()
    return (t2 - t1) / (24 * 3600 * 1000)
  },
  inWeeks: (d1: Date, d2: Date) => {
    const t2 = d2.getTime()
    const t1 = d1.getTime()
    return Math.round((t2 - t1) / (24 * 3600 * 1000 * 7))
  },
  inMonths: (d1: Date, d2: Date) => {
    const d1Y = d1.getFullYear()
    const d2Y = d2.getFullYear()
    const d1M = d1.getMonth()
    const d2M = d2.getMonth()
    return d2M + 12 * d2Y - (d1M + 12 * d1Y)
  },
  inYears: (d1: Date, d2: Date) => {
    return d2.getFullYear() - d1.getFullYear()
  },
}

/*
 * @returns {number[]} Returns tuple with year, month and day number
 */
const completeDateSchema = z
  .union([
    z.date(),
    z.string().refine((val) => {
      const parts = val.match(/\d+/g)
      return parts !== null && parts.length >= 3
    }),
  ])
  .pipe(z.coerce.date().refine((date) => isValid(date)))

/**
 * Normalizes a date value (string or Date) to a timezone-agnostic
 * local midnight, so that day-based comparisons are not affected
 * by UTC offsets or DST transitions.
 *
 * Returns undefined if the value is falsy, not a complete date
 * (year, month, day), or results in an invalid Date.
 */
export const toLocalDate = (value: unknown): Date | undefined => {
  if (!value) {
    return undefined
  }

  const parsed = completeDateSchema.safeParse(value)
  if (!parsed.success) {
    return undefined
  }

  return startOfDay(parsed.data)
}

export const parseDateValue = (dateValue: string | Date) => {
  if (dateValue === null || dateValue === undefined) {
    throw new Error("Assertion: dateValue should be a 'string' or a 'Date' type")
  }
  const date = new Date(dateValue)
  return [date.getFullYear(), date.getMonth() + 1, date.getDate()]
}

export const dateStringToDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-')
  return createSafeDate(year, month, day)
}

const ISO_DURATION_REGEX = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?$/
const DAYS_PER_YEAR = 365
const DAYS_PER_MONTH = 30
const DAYS_PER_WEEK = 7

export const parseIsoDurationToDays = (isoDuration: string): number | null => {
  const match = ISO_DURATION_REGEX.exec(isoDuration)
  if (!match || isoDuration === 'P') {
    return null
  }
  const [, years, months, weeks, days] = match
  const totalDays =
    Number(years ?? 0) * DAYS_PER_YEAR +
    Number(months ?? 0) * DAYS_PER_MONTH +
    Number(weeks ?? 0) * DAYS_PER_WEEK +
    Number(days ?? 0)
  return totalDays > 0 ? totalDays : null
}

export const isStartDateBeforeEndOfWithdrawalPeriod = (
  policyStartDate: string,
  withdrawalPeriodDays: number,
  referenceDate: Date
): boolean => {
  const endOfWithdrawalPeriod = addDays(startOfDay(referenceDate), withdrawalPeriodDays)
  return isBefore(dateStringToDate(policyStartDate), endOfWithdrawalPeriod)
}
